import ModalBase from '@/components/base/ModalBase';
import type { RadioEvent, EventParticipant } from '@/mocks/adminEvents';
import {
  eventTypeLabels, eventTypeIcons, eventTypeColors,
  eventStatusLabels, eventStatusColors,
  eventModalityLabels, eventModalityIcons, eventModalityColors,
  eventCategoryLabels, eventCategoryIcons, eventCategoryColors,
  getEventParticipants,
} from '@/mocks/adminEvents';

interface EventDetailModalProps {
  open: boolean;
  event: RadioEvent | null;
  onClose: () => void;
}

function formatMetric(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    return k >= 10 ? Math.round(k).toLocaleString() + 'K' : k.toFixed(1) + 'K';
  }
  return n.toLocaleString();
}

export default function EventDetailModal({ open, event, onClose }: EventDetailModalProps) {
  if (!open || !event) return null;

  const typeStyle = eventTypeColors[event.type];
  const statusStyle = eventStatusColors[event.status];
  const modalityStyle = eventModalityColors[event.modality];
  const participants: EventParticipant[] = getEventParticipants(event.id);
  const eventDate = new Date(event.date + 'T' + event.time);
  const hasCapacity = event.capacity > 0;
  const capacityPercent = hasCapacity ? Math.round((event.participants / event.capacity) * 100) : 0;
  const capacityFull = hasCapacity && event.participants >= event.capacity;
  const mapsQuery = event.modality !== 'online' ? encodeURIComponent(event.location) : '';

  const metricCards = [
    { icon: 'ri-user-fill', label: 'Asistentes', value: formatMetric(event.participants), raw: event.participants.toLocaleString(), color: 'text-foreground-900', bg: 'bg-foreground-50' },
    { icon: 'ri-heart-fill', label: 'Interesados', value: formatMetric(event.interested), raw: event.interested.toLocaleString(), color: 'text-rose-600', bg: 'bg-rose-50' },
    { icon: 'ri-share-forward-fill', label: 'Compartidos', value: formatMetric(event.shares), raw: event.shares.toLocaleString(), color: 'text-sky-600', bg: 'bg-sky-50' },
    { icon: 'ri-user-follow-fill', label: 'Seguidores', value: formatMetric(event.followers), raw: event.followers.toLocaleString(), color: 'text-emerald-600', bg: 'bg-emerald-50' },
  ];

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Detalle del evento"
      subtitle={event.title}
      size="lg"
      headerIcon={
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${modalityStyle.bg}`}>
          <i className={`${eventModalityIcons[event.modality]} ${modalityStyle.text} text-sm`}></i>
        </div>
      }
      footer={
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          Cerrar
        </button>
      }
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden rounded-xl mb-4">
        <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

        {/* Modality badge */}
        <div className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${modalityStyle.bg} ${modalityStyle.text} border ${modalityStyle.border} backdrop-blur-sm`}>
          <span className={`w-2 h-2 rounded-full ${modalityStyle.dot}`}></span>
          {eventModalityLabels[event.modality]}
        </div>

        {event.status === 'borrador' && (
          <div className="absolute top-3 right-3 px-2 py-0.5 rounded-md bg-foreground-900/70 text-white text-[9px] font-semibold backdrop-blur-sm">
            Borrador
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Type + Category + Status badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${typeStyle.bg} ${typeStyle.text} border ${typeStyle.border}`}>
            <i className={`${eventTypeIcons[event.type]} text-[10px]`}></i>
            {eventTypeLabels[event.type]}
          </span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${eventCategoryColors[event.category].bg} ${eventCategoryColors[event.category].text} border ${eventCategoryColors[event.category].border}`}>
            <i className={`${eventCategoryIcons[event.category]} text-[10px]`}></i>
            {eventCategoryLabels[event.category]}
          </span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`}></span>
            {eventStatusLabels[event.status]}
          </span>
        </div>

        {/* Description */}
        <p className="text-foreground-500 text-xs leading-relaxed">{event.description}</p>

        {/* 4 Metric cards */}
        <div className="grid grid-cols-4 gap-2">
          {metricCards.map((m) => (
            <div key={m.label} className={`${m.bg} rounded-xl border border-background-200/70 p-3 text-center`}>
              <i className={`${m.icon} ${m.color} text-lg mb-1 block`}></i>
              <p className={`${m.color} text-base font-bold`}>{m.value}</p>
              <p className="text-foreground-400 text-[10px] font-medium">{m.label}</p>
            </div>
          ))}
        </div>

        {/* Info */}
        <div className="space-y-2 p-3 rounded-xl bg-background-100 border border-background-200/70">
          <div className="flex items-center gap-2 text-foreground-600 text-xs">
            <i className="ri-calendar-line text-xs text-foreground-400"></i>
            <span className="font-semibold">{eventDate.toLocaleDateString('es-MX', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
          </div>
          <div className="flex items-center gap-2 text-foreground-600 text-xs">
            <i className="ri-time-line text-xs text-foreground-400"></i>
            <span className="font-semibold">{event.time} hs</span>
          </div>
          <div className="flex items-center gap-2 text-foreground-600 text-xs">
            <i className={`${eventModalityIcons[event.modality]} text-xs text-foreground-400`}></i>
            <span>{event.location}</span>
          </div>
          {hasCapacity && (
            <div>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-foreground-500">Cupo</span>
                <span className={`font-bold ${capacityFull ? 'text-red-600' : 'text-foreground-600'}`}>
                  {event.participants.toLocaleString()} / {event.capacity.toLocaleString()} ({capacityPercent}%)
                </span>
              </div>
              <div className="h-2 rounded-full bg-background-200 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${capacityFull ? 'bg-red-400' : capacityPercent >= 80 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                  style={{ width: `${Math.min(100, capacityPercent)}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Map for presencial/híbrido */}
        {event.modality !== 'online' && (
          <div>
            <h5 className="text-foreground-700 text-xs font-semibold mb-2 flex items-center gap-1.5">
              <i className="ri-map-pin-line text-xs text-foreground-400"></i>
              Ubicación en el mapa
            </h5>
            <div className="w-full h-[220px] rounded-xl overflow-hidden border border-background-200">
              <iframe
                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zEkVBtLg3zCOOgHvEFkIFcDU&q=${mapsQuery}&language=es&zoom=14`}
                title={`Mapa de ${event.title}`}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        )}

        {/* Participants */}
        <div>
          <h5 className="text-foreground-700 text-xs font-semibold mb-2">
            Asistentes confirmados ({event.participants.toLocaleString()})
          </h5>
          {participants.length === 0 ? (
            <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-foreground-100 flex items-center justify-center">
                <i className="ri-user-search-line text-xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-xs">Aún no hay asistentes confirmados</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {participants.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-background-100 border border-background-200/50">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-foreground-100 shrink-0 border border-background-200">
                    <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground-900 text-xs font-semibold">{p.name}</p>
                    <p className="text-foreground-300 text-[9px]">
                      Confirmado {new Date(p.confirmedAt).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalBase>
  );
}